import React, { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { Toaster } from "@/components/ui/toaster";
import { AuthProvider } from "@/hooks/use-auth";
import { HelmetProvider } from "react-helmet-async";

import MainLayout from "@/components/layout/MainLayout";
import { Spinner } from "@/components/ui/spinner";
import ErrorBoundary from "@/components/ErrorBoundary";
import AnalyticsRouteTracker from "@/components/AnalyticsRouteTracker";

// Only import critical paths eagerly
import Home from "@/components/layout/Home";
import NotFound from "@/pages/not-found";
import AuthPage from "@/pages/auth-page";
import VerifyEmail from "@/pages/verify-email";

// Lazy load all other pages
const ReAuthPage = lazy(() => import("@/pages/re-auth-page"));
const AccountSetup = lazy(() => import("@/pages/account-setup"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/reset-password"));
const SchedulePickup = lazy(() => import("@/pages/schedule-pickup/index"));
const StoreDetails = lazy(
  () => import("@/pages/schedule-pickup/store-details")
);
const OrderDetails = lazy(
  () => import("@/pages/schedule-pickup/order-details")
);
const DeliveryDetails = lazy(
  () => import("@/pages/schedule-pickup/delivery-details")
);
const Payment = lazy(() => import("@/pages/schedule-pickup/payment"));
const Complete = lazy(() => import("@/pages/schedule-pickup/complete"));
const ReturnsHome = lazy(() => import("@/pages/returns/ReturnsHome"));
const ReturnStoreDetailsPage = lazy(() => import("@/pages/returns/StoreDetailsPage"));
const ReturnDetailsPage = lazy(() => import("@/pages/returns/DetailsPage"));
const ReturnReviewPage = lazy(() => import("@/pages/returns/ReviewPage"));
const ReturnAuthorizePage = lazy(() => import("@/pages/returns/AuthorizePage"));
const ReturnSchedulePage = lazy(() => import("@/pages/returns/SchedulePage"));
const ReturnTrackPage = lazy(() => import("@/pages/returns/TrackPage"));
const ReturnProofPage = lazy(() => import("@/pages/returns/ProofPage"));
const ReturnRefundPage = lazy(() => import("@/pages/returns/RefundPage"));
const ReturnCompletePage = lazy(() => import("@/pages/returns/CompletePage"));
const PickupHistory = lazy(() => import("@/pages/pickup-history"));
const DeliveryTracking = lazy(() => import("@/pages/delivery-tracking"));
const Checkout = lazy(() => import("@/pages/checkout"));
const Subscribe = lazy(() => import("@/pages/subscribe"));
const Wallet = lazy(() => import("@/pages/wallet"));
const Settings = lazy(() => import("@/pages/settings"));
const StoreSelection = lazy(() => import("@/pages/store-selection"));
const RefundRequest = lazy(() => import("@/pages/refund-request"));
const DeliveryTrackingDashboard = lazy(
  () => import("@/pages/api-docs/delivery-tracking-dashboard")
);
const CheckoutWidget = lazy(() => import("@/pages/api-docs/checkout-widget"));
const APIDocumentation = lazy(
  () => import("@/pages/api-docs/full-documentation")
);

const RobotLeasing = lazy(() => import("@/pages/robot-leasing/robot-leasing"));
const RetailerOnboarding = lazy(
  () => import("@/pages/robot-leasing/retailer-onboarding")
);
const RobotLeasingPayment = lazy(
  () => import("@/pages/robot-leasing/robot-leasing-payment")
);
const RobotLeasingSuccess = lazy(
  () => import("@/pages/robot-leasing/robot-leasing-success")
);

import { API_BASE_URL } from "@/lib/api";
// Footer pages (lazy loaded — not on critical path)
const AboutUs = lazy(() => import("./pages/footer-pages/aboutUs"));
const Team = lazy(() => import("./pages/footer-pages/team"));
const HowItWorks = lazy(() => import("./pages/footer-pages/howItWorks"));
const DeliveryInfo = lazy(() => import("./pages/footer-pages/deliveryInfo"));
const HelpCenter = lazy(() => import("./pages/footer-pages/helpCenter"));
const PlatformServices = lazy(() => import("./pages/footer-pages/platformServices"));
const TermsAndConditions = lazy(() => import("./pages/footer-pages/termsAndConditions"));
const PrivacyPolicy = lazy(() => import("./pages/footer-pages/privacyPolicy"));
const AccessibilityStatement = lazy(() => import("./pages/footer-pages/accessibility"));
const CurbsidePickupPage = lazy(() => import("@/pages/curbside-pickup"));
const LandingPage = lazy(() => import("@/pages/api-landing"));

// Growth landing pages
const GetDeliveryPage = lazy(() => import("@/pages/landing/GetDeliveryPage"));
const SkipTheMallPage = lazy(() => import("@/pages/landing/SkipTheMallPage"));
const BoutiquePartnerPage = lazy(() => import("@/pages/landing/BoutiquePartnerPage"));
const CreatorCampaignPage = lazy(() => import("@/pages/landing/CreatorCampaignPage"));
const DeliveryAlertsPage = lazy(() => import("@/pages/landing/DeliveryAlertsPage"));
const VIPBetaPage = lazy(() => import("@/pages/landing/VIPBetaPage"));
const DFWCustomerPage = lazy(() => import("@/pages/landing/DFWCustomerPage"));
const DallasReturnsConciergePage = lazy(() => import("@/pages/landing/DallasReturnsConciergePage"));
const DfwFounderTalk = lazy(() => import("@/pages/events/DfwFounderTalk"));
const CmoDashboard = lazy(() => import("@/pages/cmo-dashboard"));
const CmoAutopilot = lazy(() => import("@/pages/cmo-autopilot"));

// Member experience
const MembershipChooseLane = lazy(() => import("@/pages/membership/ChooseLane"));
const MembershipCheckout = lazy(() => import("@/pages/membership/Checkout"));
const MembershipDashboard = lazy(() => import("@/pages/membership/Dashboard"));
const MemberStores = lazy(() => import("@/pages/membership/Stores"));
const MemberHowItWorks = lazy(() => import("@/pages/membership/HowItWorks"));
const MemberHelpCenter = lazy(() => import("@/pages/membership/HelpCenter"));
const MemberOrderStoreDetails = lazy(() => import("@/pages/membership/order/StoreDetails"));
const MemberOrderDetails = lazy(() => import("@/pages/membership/order/OrderDetails"));
const MemberOrderDeliveryDetails = lazy(() => import("@/pages/membership/order/DeliveryDetails"));
const MemberOrderPayment = lazy(() => import("@/pages/membership/order/Payment"));
const MemberOrderComplete = lazy(() => import("@/pages/membership/order/Complete"));

// Admin pages (lazy loaded — behind auth, not on critical path)
const AdminDashboard = lazy(() => import("./pages/admin/dashboard"));
const LiveDispatch = lazy(() => import("./pages/admin/live-dispatch"));
const AdminCoupons = lazy(() => import("./pages/admin/coupons"));
const AdminOrders = lazy(() => import("./pages/admin/orders"));
const AdminReviews = lazy(() => import("./pages/admin/reviews"));
const AgentEconomicsPage = lazy(() => import("./pages/admin/agent-economics"));
import AdminLayout from "./components/layout/AdminLayout";
import ProtectedAdminRoute from "./components/ProtectedAdminRoute";
import { ProtectedRoute } from "./components/ProtectedRoute";

// Lazy load pricing pages
const PricingSettings = lazy(() => import("./pages/admin/pricing-settings"));
const InfluencerSignup = lazy(() => import("./pages/InfluencerSignup"));
const LinksPage = lazy(() => import("./pages/LinksPage"));
const InfluencerDashboard = lazy(() => import("./pages/admin/InfluencerDashboard"));
const SurgePricing = lazy(() => import("./pages/admin/surge-pricing"));
const PricingAnalytics = lazy(() => import("./pages/admin/pricing-analytics"));
const DeliverySettings = lazy(() => import("./pages/admin/delivery-settings"));
const ScraperDashboard = lazy(() => import("./pages/admin/scraper"));

// Lazy load user management pages
const UserManagement = lazy(() => import("./pages/admin/user-management"));
const UserDetail = lazy(() => import("./pages/admin/user-detail"));
const PendingVerification = lazy(() => import("./pages/admin/pending-verification"));

// import RequestDetails from "./pages/refund-request";

// Loading fallback component
const LoadingFallback = () => (
  <div className="h-screen flex items-center justify-center">
    <div className="flex flex-col items-center">
      <Spinner className="h-10 w-10 mb-4" />
      <p className="text-gray-500">Loading...</p>
    </div>
  </div>
);

// Prefetch common data that's needed across the application
function prefetchCommonData() {
  // Prefetch stores data
  queryClient.prefetchQuery({
    queryKey: ["/api/stores"],
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const token = localStorage.getItem("token");
  if (!token) return;

  // Prefetch user's order pickups only when authenticated
  queryClient.prefetchQuery({
    queryKey: [`${API_BASE_URL}/api/order-pickups`],
    staleTime: 30 * 1000, // 30 seconds
  });
}

function App() {
  // Prefetch common data when the app loads
  useEffect(() => {
    prefetchCommonData();

    // Set up periodic refetching of data in the background
    const intervalId = setInterval(() => {
      const token = localStorage.getItem("token");
      if (!token) return;

      queryClient.invalidateQueries({
        queryKey: [`${API_BASE_URL}/api/order-pickups`],
      });
    }, 60000); // Refresh order pickups data every minute

    return () => clearInterval(intervalId);
  }, []);

  // Log initialization
  useEffect(() => {
    console.log(
      "App initialized with React Router, current path:",
      window.location.pathname
    );
  }, []);

  return (
    <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <BrowserRouter>
            <Toaster />
            <AnalyticsRouteTracker />
            <Suspense fallback={<LoadingFallback />}>
              <Routes>
                {/* Admin Routes - Must come before wildcard */}
                <Route
                  path="/admin/*"
                  element={
                    <ProtectedAdminRoute>
                      <AdminLayout>
                        <Routes>
                          <Route
                            path="dashboard"
                            element={<AdminDashboard />}
                          />
                          <Route path="coupons" element={<AdminCoupons />} />
                          <Route path="orders" element={<AdminOrders />} />
                          <Route path="reviews" element={<AdminReviews />} />
                          <Route path="pricing/settings" element={<PricingSettings />} />
                          <Route path="pricing/surge" element={<SurgePricing />} />
                          <Route path="pricing/analytics" element={<PricingAnalytics />} />
                          <Route path="delivery/settings" element={<DeliverySettings />} />
                          <Route path="stores/scraper" element={<ScraperDashboard />} />
                          <Route path="influencers" element={<InfluencerDashboard />} />
                          <Route path="users" element={<UserManagement />} />
                          <Route path="users/:userId" element={<UserDetail />} />
                          <Route path="pending-verification" element={<PendingVerification />} />
                          <Route path="agent-economics" element={<AgentEconomicsPage />} />
                          <Route path="live-dispatch" element={<LiveDispatch />} />
                          <Route path="*" element={<AdminDashboard />} />
                        </Routes>
                      </AdminLayout>
                    </ProtectedAdminRoute>
                  }
                />
                <Route
                  path="/events/dfw-startup-week-founder-talk"
                  element={<DfwFounderTalk />}
                />
                {/* Customer Routes with MainLayout */}
                <Route
                  path="/*"
                  element={
                    <MainLayout>
                      <Routes>
                        <Route path="/" element={<Home />} />
                        <Route path="/auth" element={<AuthPage />} />
                        <Route path="/register" element={<AuthPage />} />
                        <Route path="/signup" element={<AuthPage />} />
                        <Route path="/login" element={<AuthPage />} />
                        <Route path="/signin" element={<AuthPage />} />
                        <Route path="/account-setup" element={<AccountSetup />} />
                        <Route
                          path="/re-authenticate"
                          element={<ReAuthPage />}
                        />
                        <Route
                          path="/forgot-password"
                          element={<ForgotPassword />}
                        />
                        <Route
                          path="/reset-password"
                          element={<ResetPassword />}
                        />
                        {/* Schedule Pickup Flow */}
                        <Route
                          path="/schedule-pickup"
                          element={<SchedulePickup />}
                        />
                        <Route
                          path="/schedule-pickup/store-details"
                          element={<StoreDetails />}
                        />
                        <Route
                          path="/schedule-pickup/order-details"
                          element={<OrderDetails />}
                        />
                        <Route
                          path="/schedule-pickup/delivery-details"
                          element={<DeliveryDetails />}
                        />
                        <Route
                          path="/schedule-pickup/payment"
                          element={<Payment />}
                        />
                        <Route
                          path="/schedule-pickup/complete"
                          element={<Complete />}
                        />
                        {/* Returns Flow */}
                        <Route
                          path="/returns"
                          element={<ReturnsHome />}
                        />
                        <Route
                          path="/returns/store-details"
                          element={<ReturnStoreDetailsPage />}
                        />
                        <Route
                          path="/returns/details"
                          element={<ReturnDetailsPage />}
                        />
                        <Route
                          path="/returns/review"
                          element={<ReturnReviewPage />}
                        />
                        <Route
                          path="/returns/authorize"
                          element={<ReturnAuthorizePage />}
                        />
                        <Route
                          path="/returns/schedule"
                          element={<ReturnSchedulePage />}
                        />
                        <Route
                          path="/returns/track"
                          element={<ReturnTrackPage />}
                        />
                        <Route
                          path="/returns/proof"
                          element={<ReturnProofPage />}
                        />
                        <Route
                          path="/returns/refund"
                          element={<ReturnRefundPage />}
                        />
                        <Route
                          path="/returns/complete"
                          element={<ReturnCompletePage />}
                        />
                        {/* Other Routes */}
                        <Route
                          path="/store-selection"
                          element={<StoreSelection />}
                        />
                        <Route path="/checkout" element={<Checkout />} />
                        <Route path="/subscribe" element={<Subscribe />} />
                        <Route
                          path="/members"
                          element={
                            <ProtectedRoute>
                              <MembershipDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member"
                          element={
                            <ProtectedRoute>
                              <MembershipDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/membership"
                          element={
                            <ProtectedRoute>
                              <MembershipDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/membership/choose-lane"
                          element={<MembershipChooseLane />}
                        />
                        <Route
                          path="/membership/checkout"
                          element={
                            <ProtectedRoute>
                              <MembershipCheckout />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/membership/dashboard"
                          element={
                            <ProtectedRoute>
                              <MembershipDashboard />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/stores"
                          element={
                            <ProtectedRoute>
                              <MemberStores />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/stores"
                          element={
                            <ProtectedRoute>
                              <MemberStores />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/how-it-works"
                          element={
                            <ProtectedRoute>
                              <MemberHowItWorks />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/help-center"
                          element={
                            <ProtectedRoute>
                              <MemberHelpCenter />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/help"
                          element={
                            <ProtectedRoute>
                              <MemberHelpCenter />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/benefits"
                          element={
                            <ProtectedRoute>
                              <MembershipChooseLane />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/order/store-details"
                          element={
                            <ProtectedRoute>
                              <MemberOrderStoreDetails />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/order/order-details"
                          element={
                            <ProtectedRoute>
                              <MemberOrderDetails />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/order/delivery-details"
                          element={
                            <ProtectedRoute>
                              <MemberOrderDeliveryDetails />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/order/payment"
                          element={
                            <ProtectedRoute>
                              <MemberOrderPayment />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/order/complete"
                          element={
                            <ProtectedRoute>
                              <MemberOrderComplete />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/returns"
                          element={
                            <ProtectedRoute>
                              <ReturnsHome />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/returns/store-details"
                          element={
                            <ProtectedRoute>
                              <MemberOrderStoreDetails />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/returns/order-details"
                          element={
                            <ProtectedRoute>
                              <MemberOrderDetails />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/returns/delivery-details"
                          element={
                            <ProtectedRoute>
                              <MemberOrderDeliveryDetails />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/returns/payment"
                          element={
                            <ProtectedRoute>
                              <MemberOrderPayment />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/member/returns/complete"
                          element={
                            <ProtectedRoute>
                              <MemberOrderComplete />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/pickup-history"
                          element={
                            <ProtectedRoute>
                              <PickupHistory />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/delivery-tracking/:id"
                          element={<DeliveryTracking />}
                        />
                        <Route
                          path="/wallet"
                          element={
                            <ProtectedRoute>
                              <Wallet />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/settings"
                          element={
                            <ProtectedRoute>
                              <Settings />
                            </ProtectedRoute>
                          }
                        />
                        <Route
                          path="/refund-request"
                          element={<RefundRequest />}
                        />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route
                          path="/curbside-pickup"
                          element={<CurbsidePickupPage />}
                        />
                        {/* Growth Landing Pages — no auth required */}
                        <Route path="/get-delivery" element={<GetDeliveryPage />} />
                        <Route path="/skip-the-mall" element={<SkipTheMallPage />} />
                        <Route path="/boutique-partner" element={<BoutiquePartnerPage />} />
                        <Route path="/creator-campaign" element={<CreatorCampaignPage />} />
                        <Route path="/delivery-alerts" element={<DeliveryAlertsPage />} />
                        <Route path="/vip" element={<VIPBetaPage />} />
                        <Route path="/dfw" element={<DFWCustomerPage />} />
                        <Route path="/dallas-returns-concierge" element={<DallasReturnsConciergePage />} />
                        <Route path="/cmo-dashboard" element={<CmoDashboard />} />
                        <Route path="/cmo-autopilot" element={<CmoAutopilot />} />
                        {/* API Docs Pages */}
                        <Route path="/landing" element={<LandingPage />} />
                        <Route path="/collab" element={<InfluencerSignup />} />
                        <Route path="/links" element={<LinksPage />} />
                        <Route
                          path="/checkout-widget"
                          element={<CheckoutWidget />}
                        />
                        <Route
                          path="/api-docs"
                          element={<APIDocumentation />}
                        />
                        <Route
                          path="/delivery-tracking-dashboard"
                          element={<DeliveryTrackingDashboard />}
                        />
                        {/* Footer Pages */}
                        <Route path="/about-us" element={<AboutUs />} />
                        <Route
                          path="/platform-services"
                          element={<PlatformServices />}
                        />
                        <Route path="/how-it-works" element={<HowItWorks />} />
                        <Route path="/team" element={<Team />} />
                        <Route path="/help-center" element={<HelpCenter />} />
                        <Route
                          path="/delivery-info"
                          element={<DeliveryInfo />}
                        />
                        <Route
                          path="/robot-leasing"
                          element={<RobotLeasing />}
                        />
                        {/* Robot Leasing Pages */}
                        <Route
                          path="/retailer-onboarding"
                          element={<RetailerOnboarding />}
                        />
                        <Route
                          path="/robot-leasing-payment"
                          element={<RobotLeasingPayment />}
                        />
                        <Route
                          path="/robot-leasing-success"
                          element={<RobotLeasingSuccess />}
                        />
                        <Route
                          path="/terms-and-conditions"
                          element={<TermsAndConditions />}
                        />
                        <Route
                          path="/privacy-policy"
                          element={<PrivacyPolicy />}
                        />
                        <Route
                          path="/accessibility"
                          element={<AccessibilityStatement />}
                        />
                        <Route path="*" element={<NotFound />} />
                      </Routes>
                    </MainLayout>
                  }
                />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
